import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, ArrowRight, FileText, Target, Rocket, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { businessApi } from '../../lib/api';
import { SELECTED_TIER_KEY } from '../Pricing';
import { trackPurchase } from '../../utils/analytics';
import { clearUrlSecret, readUrlSecret } from '../../lib/urlSecrets';

const MAX_POLLS = 15; // ~30s; after that we stop spinning and point to the dashboard

const SubscriptionThankYou = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  // Stripe's ?session_id=: in the URL, or kept for this page by the index.html URL secret guard
  // (lib/urlSecrets); read once, and the kept copy is cleared once the payment is confirmed.
  const [sessionId] = useState(() => readUrlSecret('session_id', { location }));
  
  // Initialize state based on whether we have a session ID
  const [isVerifying, setIsVerifying] = useState(() => !!sessionId);
  const [verificationStatus, setVerificationStatus] = useState(() => sessionId ? 'loading' : 'error');
  const pollingRef = useRef(null);
  const pollCount = useRef(0);
  const [planName, setPlanName] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const response = await businessApi.get(`/stripe/checkout-status/${sessionId}`);

        if (response.data.payment_status === 'paid') {
          clearUrlSecret('session_id');
          setVerificationStatus('success');
          setIsVerifying(false);
          trackPurchase({ planTier: response.data.metadata?.plan_tier });
          // The plan is bought: the pre-checkout tier choice must not leak into a later signup.
          try { localStorage.removeItem(SELECTED_TIER_KEY); } catch { /* private mode */ }
          try {
            const status = await businessApi.get('/business/account-status');
            setPlanName(status.data?.plan_name || null);
          } catch { /* plan name is cosmetic */ }
        } else if (pollCount.current >= MAX_POLLS) {
          // Payment recorded by Stripe but activation is lagging (webhook delay). Don't spin forever.
          setVerificationStatus('slow');
          setIsVerifying(false);
        } else {
          pollCount.current += 1;
          pollingRef.current = setTimeout(verifyPayment, 2000);
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setVerificationStatus('error');
        setIsVerifying(false);
      }
    };

    if (sessionId) {
      verifyPayment();
    }

    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, [sessionId]);

  // Phase 1: onboarding happens BEFORE checkout, so the only next step is the dashboard.
  const handleGoToDashboard = async () => {
    try {
      await refreshUser();
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
    navigate('/business/dashboard');
  };

  // V5 Loading State
  if (isVerifying) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#FEF7ED' }}
      >
        <div className="text-center">
          {/* Tappd Logo */}
          <img 
            src="/tappd-logo.png" 
            alt="Tappd" 
            className="h-12 mx-auto mb-6"
          />
          <Loader2 
            className="mx-auto mb-4 animate-spin" 
            size={48} 
            style={{ color: '#8b5cf6' }} 
          />
          <p 
            className="text-[14px]"
            style={{ color: 'rgba(30, 27, 75, 0.7)' }}
          >
            Verifying your payment...
          </p>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error' || verificationStatus === 'slow') {
    const slow = verificationStatus === 'slow';
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center shadow-sm border border-[#1e1b4b]/8">
          <AlertCircle size={40} className="mx-auto mb-4" style={{ color: slow ? '#f59e0b' : '#dc2626' }} />
          <h1 className="text-2xl font-bold text-[#1e1b4b] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            {slow ? 'Payment received — activating' : "We couldn't confirm your payment"}
          </h1>
          <p className="text-[15px] text-[#1e1b4b]/70 mb-6">
            {slow
              ? 'Stripe has your payment. Activation usually takes a few seconds; your dashboard will show the plan shortly.'
              : 'If you completed checkout, your plan will appear on your dashboard within a few minutes. If you were not charged, you can choose a plan again.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={handleGoToDashboard} className="px-6 py-3 rounded-xl font-bold text-white" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>Go to dashboard</button>
            {!slow && <button onClick={() => navigate('/business/subscription')} className="px-6 py-3 rounded-xl font-bold border border-[#1e1b4b]/15 text-[#1e1b4b]">Choose a plan</button>}
          </div>
          <p className="mt-6 text-[13px] text-[#1e1b4b]/50">Need help? <a href="mailto:hello@tappd.app" className="underline">hello@tappd.app</a></p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-16 px-4"
      style={{ backgroundColor: '#FEF7ED' }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Success Header with Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-12"
        >
          {/* Tappd Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <img 
              src="/tappd-logo.png" 
              alt="Tappd" 
              className="h-16 mx-auto"
            />
          </motion.div>

          {/* V5 Code Accent */}
          <p className="font-mono text-xs mb-3" style={{ color: '#8b5cf6' }}>
            // subscription_complete
          </p>

          {/* Welcome Text */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[13px] uppercase tracking-widest mb-3"
            style={{ color: 'rgba(30, 27, 75, 0.5)' }}
          >
            Welcome
          </motion.p>

          {/* Main Heading with Checkmark */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center justify-center gap-4 mb-4"
          >
            <h1
              className="text-[32px] md:text-[40px]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
            >
              You&apos;re All Set!
            </h1>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.5 }}
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
            >
              <CheckCircle size={32} style={{ color: '#22c55e' }} strokeWidth={2.5} />
            </motion.div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-[16px] max-w-xl mx-auto"
            style={{ color: 'rgba(30, 27, 75, 0.7)' }}
          >
            Thank you for subscribing. Your <span className="font-semibold" style={{ color: '#8b5cf6' }}>{planName || 'Tappd'} plan</span> is active. Here's what happens next.
          </motion.p>

          {/* Celebration confetti dots - V5 colors */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex justify-center gap-2 mt-6"
          >
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 + i * 0.1 }}
                className="w-2 h-2 rounded-full"
                style={{ 
                  backgroundColor: ['#f97316', '#22c55e', '#8b5cf6', '#14b8a6', '#8b5cf6'][i] 
                }}
              />
            ))}
          </motion.div>
        </motion.div>

        {/* Video Section - V5 Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white p-8 mb-8"
          style={{ 
            borderRadius: '12px',
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #22c55e',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          <h2 
            className="text-[24px] mb-6 text-center"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
          >
            What happens <span style={{ color: '#8b5cf6' }}>next</span>
          </h2>
          
          {/* Quick Tips - V5 Step Cards */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Step 1 - Orange (Starter tier color) */}
            <motion.div 
              whileHover={{ y: -3 }}
              className="text-center p-5 bg-white"
              style={{ 
                borderRadius: '12px',
                border: '1px solid rgba(30, 27, 75, 0.05)',
                borderLeft: '4px solid #f97316',
                boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
              }}
            >
              <div 
                className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                style={{ 
                  backgroundColor: 'rgba(249, 115, 22, 0.1)',
                  borderRadius: '12px'
                }}
              >
                <FileText size={24} style={{ color: '#f97316' }} />
              </div>
              <h3 
                className="font-bold text-[14px] mb-1"
                style={{ color: '#1e1b4b' }}
              >
                Step 1: Your details are in
              </h3>
              <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                Business profile, venues and first deal are saved
              </p>
            </motion.div>

            {/* Step 2 - Purple */}
            <motion.div 
              whileHover={{ y: -3 }}
              className="text-center p-5 bg-white"
              style={{ 
                borderRadius: '12px',
                border: '1px solid rgba(30, 27, 75, 0.05)',
                borderLeft: '4px solid #8b5cf6',
                boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
              }}
            >
              <div 
                className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                style={{ 
                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '12px'
                }}
              >
                <Target size={24} style={{ color: '#8b5cf6' }} />
              </div>
              <h3 
                className="font-bold text-[14px] mb-1"
                style={{ color: '#1e1b4b' }}
              >
                Step 2: We review your listing
              </h3>
              <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                Usually within one business day
              </p>
            </motion.div>

            {/* Step 3 - Green (Success) */}
            <motion.div 
              whileHover={{ y: -3 }}
              className="text-center p-5 bg-white"
              style={{ 
                borderRadius: '12px',
                border: '1px solid rgba(30, 27, 75, 0.05)',
                borderLeft: '4px solid #22c55e',
                boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
              }}
            >
              <div 
                className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                style={{ 
                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                  borderRadius: '12px'
                }}
              >
                <Rocket size={24} style={{ color: '#22c55e' }} />
              </div>
              <h3 
                className="font-bold text-[14px] mb-1"
                style={{ color: '#1e1b4b' }}
              >
                Step 3: Your deal goes live
              </h3>
              <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                At the venues you picked, as soon as review is done
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* CTA Button - V5 Purple Primary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <motion.button
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGoToDashboard}
            className="px-12 py-4 text-[16px] font-bold text-white flex items-center justify-center gap-3 mx-auto transition-all"
            style={{
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
            }}
          >
            Go to your dashboard
            <ArrowRight size={20} />
          </motion.button>

          <p 
            className="mt-6 text-[14px]"
            style={{ color: 'rgba(30, 27, 75, 0.5)' }}
          >
            Track claims and redemptions, edit your deal, or manage billing from your dashboard
          </p>
        </motion.div>

        {/* Additional Info - V5 Style */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-12 text-center"
        >
          <div 
            className="inline-flex items-center gap-2 px-6 py-3 bg-white"
            style={{ 
              borderRadius: '9999px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ backgroundColor: '#22c55e' }}
            ></div>
            <span className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
              <strong style={{ color: '#1e1b4b' }}>Next:</strong> your deal goes live once our team reviews your listing
            </span>
          </div>
          <p className="mt-4 text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            Need help? Contact us at <a href="mailto:hello@tappd.app" className="hover:underline font-medium" style={{ color: '#8b5cf6' }}>hello@tappd.app</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SubscriptionThankYou;
